/**
 * Canonical movement — exact mirror of contracts/src/libraries/MovementLib.sol.
 * BigInt `/` truncates toward zero, identical to Solidity signed/unsigned division.
 */
import {
  type CourseObject,
  type Crossing,
  EncounterKind,
  type HandlerParams,
  type MovementParams,
  Route,
  type RunnerState,
  type StepOutput,
} from './types.js';

export const MAX_CROSSINGS = 8;
export const MAX_PACE = 210n;
/** Engine v2+ puddle effect. */
export const PUDDLE_SLOW_PCT = 10n;
export const PUDDLE_SLOW_STEPS = 1n;
const PPM = 1_000_000n;

export const clamp = (v: bigint, lo: bigint, hi: bigint): bigint => (v < lo ? lo : v > hi ? hi : v);

export function lateralInput(net: bigint, refSupply: bigint, p: MovementParams): bigint {
  const raw = (net * PPM * p.sensitivity) / refSupply;
  return clamp(raw, -p.uMax, p.uMax);
}

export function pace(p: MovementParams, volume: bigint, refSupply: bigint, slowPct: bigint): bigint {
  const volPpm = (volume * PPM) / refSupply;
  const bonus = volPpm >= p.paceBonusVolumePpm ? p.paceBonusMax : (p.paceBonusMax * volPpm) / p.paceBonusVolumePpm;
  return ((p.basePace + bonus) * (100n - slowPct)) / 100n;
}

function applySlow(n: RunnerState, pct: bigint, steps: bigint): void {
  if (n.slowSteps === 0n || pct > n.slowPct) n.slowPct = pct;
  if (steps > n.slowSteps) n.slowSteps = steps;
}

export class TooManyCrossingsError extends Error {
  constructor() {
    super('TooManyCrossings');
  }
}

/**
 * Advance one resolved step. Pure: `s` is not mutated.
 * @param rules engine rules version (RunEngineV1 = 1, RunEngineV2 = 2 adds PUDDLE). Version 1 behaviour is frozen.
 */
export function step(
  s: RunnerState,
  objs: CourseObject[],
  cursor: number,
  net: bigint,
  volume: bigint,
  refSupply: bigint,
  p: MovementParams,
  h: HandlerParams,
  rules = 1,
): StepOutput {
  const n: RunnerState = { ...s };

  const slow = n.slowSteps > 0n ? n.slowPct : 0n;
  if (n.slowSteps > 0n) {
    n.slowSteps -= 1n;
    if (n.slowSteps === 0n) n.slowPct = 0n;
  }
  if (n.overheatSteps > 0n) n.overheatSteps -= 1n;

  const u = lateralInput(net, refSupply, p);
  const vxCap = p.vxMax;
  let pressure = (n.pressure * p.decayPpm) / PPM + u;
  pressure = clamp(pressure, -4n * vxCap, 4n * vxCap);
  const vx = clamp(pressure, -vxCap, vxCap);

  const x0 = n.x;
  let x1 = clamp(x0 + vx, -p.xMax, p.xMax);
  if (n.inSplit) {
    if (n.route === Route.FORGE) x1 = clamp(x1, -p.xMax, -p.forkMedian);
    else if (n.route === Route.HARVEST) x1 = clamp(x1, p.forkMedian, p.xMax);
  }

  const pc = pace(p, volume, refSupply, slow);
  const y0 = n.y;
  const y1 = y0 + pc;

  const crossings: Crossing[] = [];
  let i = cursor;
  while (i < objs.length && objs[i].y <= y1) {
    if (crossings.length === MAX_CROSSINGS) throw new TooManyCrossingsError();
    const o = objs[i];
    const xc = x0 + ((x1 - x0) * (o.y - y0)) / (y1 - y0);
    const inside = xc >= o.xL - p.halfWidth && xc <= o.xR + p.halfWidth;
    let hit = inside;

    if (o.kind === EncounterKind.FORK) {
      n.route = xc >= 0n ? Route.HARVEST : Route.FORGE;
      n.inSplit = true;
      hit = true;
    } else if (o.kind === EncounterKind.MERGE) {
      n.inSplit = false;
      hit = true;
    } else if (o.kind === EncounterKind.BARRIER) {
      if (inside) {
        n.stumbleUntilY = o.y + p.stumbleDistance;
        applySlow(n, h.barrierSlowPct, h.barrierSlowSteps);
      }
    } else if (o.kind === EncounterKind.BURNING_BARRICADE) {
      if (inside && n.overheatSteps === 0n) {
        n.overheatSteps = h.overheatSteps;
        applySlow(n, h.overheatSlowPct, h.overheatSteps);
      }
    } else if (o.kind === EncounterKind.WATER_STATION) {
      if (inside) {
        n.slowPct = 0n;
        n.slowSteps = 0n;
        n.overheatSteps = 0n;
      }
    } else if (o.kind === EncounterKind.GOLDEN_SHOE) {
      hit = inside && o.y >= n.stumbleUntilY;
    } else if (o.kind === EncounterKind.FINISH) {
      hit = true;
    } else if (o.kind === EncounterKind.PUDDLE && rules >= 2) {
      if (inside) applySlow(n, PUDDLE_SLOW_PCT, PUDDLE_SLOW_STEPS);
    }

    crossings.push({ index: i, kind: o.kind, hit, xCross: xc });
    i++;
  }

  n.x = x1;
  n.y = y1;
  n.vx = vx;
  n.pressure = pressure;
  return { state: n, pace: pc, u, cursor: i, crossings };
}

/** Lateral position at forward coordinate `y` on the segment of a resolved step (render helper, same rounding). */
export function lateralAt(x0: bigint, y0: bigint, x1: bigint, y1: bigint, y: bigint): bigint {
  if (y1 === y0) return x1;
  return x0 + ((x1 - x0) * (y - y0)) / (y1 - y0);
}
